import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { updateVolunteerFormSchema, UpdateVolunteerFormType, VolunteerFormType, volunteersFormSchema } from '../../schemas/volunteers.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateVolunteer } from '../../actions/volunteers.actions'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { useQueryClient } from '@tanstack/react-query'
import { ADMIN } from '@/shared/constants/backend-api'
import { USER_WITH_EMAIL_ALREADY_EXISTS_ERROR } from '@/shared/constants/error-messages'
import VolunteerBaseForm from './volunteer-base-form'
import { Volunteer } from '../../types/volunteers.type'
import { VOLUNTEER_ROLE_DESCRIPTION } from '@/shared/constants/user-roles'
import { getDirtyFieldsValues } from '@/shared/lib/utils'
import { parseServePeriodBoundary } from '../../lib/merge-serve-periods-for-form'
import { sortServePeriodsChronologically } from '../../lib/serve-periods-display'


type UpdateVolunteerFormProps = {
    onSuccessCallback?: () => void;
    volunteer: Volunteer
}

function UpdateVolunteerForm({ onSuccessCallback, volunteer }: UpdateVolunteerFormProps) {
    const queryClient = useQueryClient()

    const form = useForm<UpdateVolunteerFormType>({
        resolver: zodResolver(updateVolunteerFormSchema),
        defaultValues: {
            role: volunteer?.user?.role.id || VOLUNTEER_ROLE_DESCRIPTION.id,
            fullName: volunteer?.user?.fullname || "",
            email: volunteer?.user?.email || "",
            phone: volunteer?.user?.phone ?? "",
            preferredLocation: volunteer?.preferredLocation ?? "",
            organizationName: volunteer?.organizationName ?? "",
            dateOfBirth: (() => {
                const dob = volunteer?.user?.dateOfBirth
                if (!dob) return undefined
                if ((dob as unknown) instanceof Date) return dob as unknown as Date
                if (typeof dob === 'string') {
                    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
                        const [y, m, d] = dob.split('-').map(Number)
                        return new Date(y, m - 1, d)
                    }
                    return new Date(dob)
                }
                return undefined
            })(),
            gender: volunteer?.gender ?? undefined,
            servePeriods: (() => {
                const serveDates = volunteer?.serveDates
                if (serveDates && Array.isArray(serveDates) && serveDates.length > 0) {
                    const mapped = serveDates.map((period: any) => {
                        const regionId = period.regionId ?? period.region?.id
                        return {
                            from: parseServePeriodBoundary(period.from),
                            to: parseServePeriodBoundary(period.to),
                            regionId: regionId != null ? (typeof regionId === 'number' ? regionId : parseInt(String(regionId), 10)) : undefined,
                        }
                    })
                    return sortServePeriodsChronologically(mapped)
                }
                return [{ from: undefined, to: undefined, regionId: undefined }]
            })(),
            profilePicture: volunteer?.user?.profilePicture
        }
    })
    const { handleSubmit, formState: { errors, dirtyFields } } = form


    const onSubmit = async (data: UpdateVolunteerFormType) => {
        const updatedForm = getDirtyFieldsValues(dirtyFields, data)
        const response = await updateVolunteer(volunteer.id, updatedForm)
        if (!response.success) {
            errorToast(response.message)
            return
        }
        successToast("Volunteer updated successfully")
        queryClient.invalidateQueries({ queryKey: [ADMIN.base] })
        form.reset(data)
        if (onSuccessCallback) {
            onSuccessCallback()
        }
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <VolunteerBaseForm isUpdate={true} />
            </form>
        </FormProvider>
    )
}

export default UpdateVolunteerForm